import React, { useEffect, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { dossierApi, imageUrl, PLACEHOLDER_IMAGE } from "../../lib/api";
import { useToast } from "../../hooks/use-toast";
import { Toaster } from "../../components/ui/toaster";
import { FolderOpen, CalendarCheck, CalendarX, ChevronRight, Archive, ArchiveRestore } from "lucide-react";

const fmtDate = (d) => (d ? new Date(d).toLocaleDateString("nl-BE") : "—");

function ProgressBar({ progress }) {
  const pct = Math.round((progress.completed / progress.total) * 100);
  return (
    <div className="w-full">
      <div className="flex items-center justify-between text-[11px] text-white/40 mb-1">
        <span>{progress.completed}/{progress.total} stappen</span>
        <span>{pct}%</span>
      </div>
      <div className="h-1.5 bg-white/10 rounded-full overflow-hidden">
        <div className="h-full bg-[#c5a76f] rounded-full transition-all" style={{ width: `${pct}%` }} />
      </div>
    </div>
  );
}

export default function AdminDossiers() {
  const { toast } = useToast();
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [cal, setCal] = useState({ configured: false, connected: false });
  const [params] = useSearchParams();
  const [view, setView] = useState("active");
  const [busyId, setBusyId] = useState(null);

  const load = (v = view) => {
    setLoading(true);
    dossierApi.listByView(v).then(setItems).finally(() => setLoading(false));
  };

  useEffect(() => {
    load(view);
    dossierApi.calendarStatus().then(setCal).catch(() => {});
    /* eslint-disable-next-line */
  }, [view]);

  useEffect(() => {
    const c = params.get("calendar");
    if (c === "connected") toast({ title: "Google Calendar gekoppeld" });
    if (c === "error") toast({ title: "Koppeling mislukt", description: "Probeer opnieuw." });
  }, [params, toast]);

  const toggleArchive = async (e, d) => {
    e.stopPropagation();
    setBusyId(d.id);
    try {
      if (d.archived) {
        await dossierApi.unarchive(d.id);
        toast({ title: "Teruggezet", description: `"${d.car_title}" staat weer bij actief.` });
      } else {
        await dossierApi.archive(d.id);
        toast({ title: "Gearchiveerd", description: `"${d.car_title}" is gearchiveerd.` });
      }
      load(view);
    } catch (err) {
      toast({ title: "Mislukt", description: err?.response?.data?.detail || String(err) });
    } finally {
      setBusyId(null);
    }
  };

  const connectCalendar = async () => {
    try {
      const { authorization_url } = await dossierApi.calendarConnect();
      window.location.href = authorization_url;
    } catch (e) {
      toast({ title: "Kan niet koppelen", description: e?.response?.data?.detail || "Google Calendar nog niet geconfigureerd." });
    }
  };

  return (
    <div className="p-6 md:p-8" data-testid="admin-dossiers-page">
      <Toaster />
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h1 className="text-3xl font-bold text-white mb-1" style={{ fontFamily: "'Familjen Grotesk', serif" }}>
            Leverings<span className="text-[#c5a76f]">dossiers</span>
          </h1>
          <p className="text-white/50 text-sm">{items.length} {view === "archived" ? "gearchiveerde" : "actieve"} dossiers · klantopvolging na verkoop</p>
        </div>
        <button
          onClick={connectCalendar}
          data-testid="calendar-connect-btn"
          className={`flex items-center gap-2 px-4 py-2 rounded text-sm border transition-colors ${
            cal.connected
              ? "border-emerald-500/30 text-emerald-300 bg-emerald-500/10"
              : "border-white/15 text-white/70 hover:bg-white/5"
          }`}
        >
          {cal.connected ? <CalendarCheck className="w-4 h-4" /> : <CalendarX className="w-4 h-4" />}
          {cal.connected ? "Agenda gekoppeld" : "Google Agenda koppelen"}
        </button>
      </div>

      {/* View tabs */}
      <div className="mt-5 inline-flex bg-[#242424] border border-white/10 rounded-lg p-1">
        {[["active", "Actief"], ["archived", "Gearchiveerd"]].map(([v, lbl]) => (
          <button
            key={v}
            onClick={() => setView(v)}
            data-testid={`dossier-tab-${v}`}
            className={`px-4 py-1.5 rounded-md text-sm transition-colors ${
              view === v ? "bg-[#c5a76f] text-black font-semibold" : "text-white/60 hover:text-white"
            }`}
          >
            {lbl}
          </button>
        ))}
      </div>

      {loading ? (
        <div className="text-white/50 mt-8">Laden...</div>
      ) : items.length === 0 ? (
        <div className="mt-10 text-center text-white/40">
          <FolderOpen className="w-10 h-10 mx-auto mb-3 opacity-40" />
          {view === "archived"
            ? "Geen gearchiveerde dossiers."
            : "Nog geen dossiers. Een dossier wordt automatisch aangemaakt wanneer je een wagen als verkocht markeert."}
        </div>
      ) : (
        <div className="mt-6 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
          {items.map((d) => (
            <div
              key={d.id}
              onClick={() => navigate(`/admin/dossiers/${d.id}`)}
              data-testid={`dossier-card-${d.id}`}
              className="cursor-pointer text-left bg-[#242424] border border-white/5 rounded-md overflow-hidden hover:border-[#c5a76f]/40 transition-colors group"
            >
              <div className="relative aspect-[16/9] bg-[#1a1a1a]">
                <img src={d.car_image ? imageUrl(d.car_image, { thumb: true }) : PLACEHOLDER_IMAGE} alt="" className="w-full h-full object-cover" />
                <div className="absolute inset-0 bg-gradient-to-t from-black/70 to-transparent" />
                <ChevronRight className="absolute top-3 right-3 w-5 h-5 text-white/60 group-hover:text-[#c5a76f]" />
                <button
                  onClick={(e) => toggleArchive(e, d)}
                  disabled={busyId === d.id}
                  data-testid={`dossier-archive-${d.id}`}
                  title={d.archived ? "Terugzetten naar actief" : "Archiveren"}
                  className="absolute bottom-3 right-3 flex items-center gap-1.5 px-2.5 py-1.5 rounded bg-black/60 backdrop-blur text-white/80 hover:text-[#c5a76f] text-xs border border-white/15 disabled:opacity-50"
                >
                  {d.archived ? <ArchiveRestore className="w-3.5 h-3.5" /> : <Archive className="w-3.5 h-3.5" />}
                  {d.archived ? "Terugzetten" : "Archiveren"}
                </button>
              </div>
              <div className="p-4 space-y-3">
                <div>
                  <h3 className="text-white text-base font-semibold leading-tight">{d.car_title}</h3>
                  <div className="text-xs text-white/50 mt-0.5">
                    {d.customer?.name || "Geen klantnaam"} · {fmtDate(d.created_at)}
                  </div>
                </div>
                <ProgressBar progress={d.progress} />
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
